/**
 * i18n Context and Hook for internationalization
 */
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Language, TranslationStrings, SUPPORTED_LANGUAGES } from './types';
import { en } from './locales/en';
import { hi } from './locales/hi';
import { ta } from './locales/ta';
import { te } from './locales/te';
import { mr } from './locales/mr';
import { bn } from './locales/bn';
import { gu } from './locales/gu';
import { kn } from './locales/kn';
import { ml } from './locales/ml';
import { pa } from './locales/pa';

const DEFAULT_LANGUAGE: Language = 'en';

// Translation map
const translations: Record<Language, TranslationStrings> = {
  en,
  hi,
  ta,
  te,
  mr,
  bn,
  gu,
  kn,
  ml,
  pa,
};

// Get translation helper
function getTranslation(lang: Language = DEFAULT_LANGUAGE): TranslationStrings {
  return translations[lang] || translations[DEFAULT_LANGUAGE];
}

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationStrings;
  // Helper to interpolate variables in strings
  formatMessage: (template: string, values?: Record<string, string | number>) => string;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = 'tgp_consent_language';

// Detect browser language
const detectBrowserLanguage = (): Language => {
  const browserLang = navigator.language.split('-')[0] as Language;
  const supported = SUPPORTED_LANGUAGES.map(l => l.code);
  return supported.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE;
};

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ 
  children, 
  defaultLanguage 
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first
    try {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
      if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
        return saved;
      }
    } catch (e) {
      // localStorage not available
    }
    // Fall back to provided default or browser detection
    return defaultLanguage || detectBrowserLanguage();
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch (e) {
      // localStorage not available
    }
    // Update document lang attribute for accessibility
    document.documentElement.lang = lang;
  }, []);

  // Set initial document language
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = getTranslation(language);

  // Helper to interpolate variables like {current} and {total}
  const formatMessage = useCallback((template: string, values?: Record<string, string | number>): string => {
    if (!values) return template;
    return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`));
  }, []);

  const value: I18nContextValue = {
    language,
    setLanguage,
    t,
    formatMessage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Hook to use i18n
export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Hook for components that need i18n but may be outside provider (graceful fallback)
export const useI18nSafe = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    // Return a fallback context with English translations
    return {
      language: DEFAULT_LANGUAGE,
      setLanguage: () => {},
      t: getTranslation(DEFAULT_LANGUAGE),
      formatMessage: (template: string, values?: Record<string, string | number>) => {
        if (!values) return template;
        return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`));
      },
      supportedLanguages: SUPPORTED_LANGUAGES,
    };
  }
  return context;
};

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, translations, getTranslation };
export type { Language, TranslationStrings };
